import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import Button from "components/form/Button";
import { BudgetCalculatorIcon, OfferIcon, RankingIcon } from "utils/icon";
import BudgetCalc from "./BudgetCalc";
import Skeleton from "react-loading-skeleton";
import { HandleTabChangeType } from "pages/makePayment";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { SettingsValue } from "types/payments";
import { formatMinAndMaxAmount } from "utils/HelperFunctions";
import { useAppSelector } from "store/hooks";
import { RootState } from "store/store";
import { useUserData } from "store/slices/authSlice";
import {
  BudgetCalculatorAssistanceReason,
  getBudgetCalculatorAssistanceMessage,
  getBudgetCalculatorLimitResult,
  getNonBudgetOfferTransactionLimitError,
} from "utils/selectedOfferLimits";

interface AvailableOfferProps {
  setActiveOffer: (offer: string) => void;
  activeOffer: string;
  availableStateLoading: boolean;
  availableOfferData: any;
  setSelectedOffer: (offer: any) => void;
  availableOffer: boolean;
  setAvailableOffer: Dispatch<SetStateAction<boolean>>;
  selectedOffer: any;
  setTab: Dispatch<SetStateAction<HandleTabChangeType>>;
  setBudgetCalculatorIncome?: any;
  budgerCalculatorIncome?: any;
  setDataForSelectedOffer?: (selectedOffer: any) => void;
  setOfferStep?: any;
  handleSendAssistanceRequest: (
    income: number,
    expense: number,
    netIncome: number
  ) => void;
  isAssistanceLoading: boolean;
}

interface PaymentAssistanceState {
  isNeeded: boolean;
  reason: BudgetCalculatorAssistanceReason | null;
}

const AvailableOffer: React.FC<AvailableOfferProps> = (props) => {
  const {
    setActiveOffer,
    activeOffer,
    availableStateLoading,
    availableOfferData,
    setSelectedOffer,
    setAvailableOffer,
    selectedOffer,
    setTab,
    setBudgetCalculatorIncome,
    budgerCalculatorIncome,
    setDataForSelectedOffer = () => {},
    setOfferStep = () => {},
    isAssistanceLoading,
    handleSendAssistanceRequest,
  } = props;

  const offerGroupId = useId().replace(/:/g, "");
  const budgetCalculatorHeadingRef = useRef<HTMLHeadingElement>(null);
  const offerInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const wasBudgetCalculatorOpenRef = useRef(false);
  const portalSettings = usePortalSettingsData();
  const [incomeAndExpense, setIncomeAndExpense] = useState<{
    income: number;
    expense: number;
    netIncome: number;
  }>({ income: 0, expense: 0, netIncome: 0 });

  const { generalSettings } = useAppSelector(
    (state: RootState) => state.payment
  );
  const userData = useUserData();

  const handleOfferChange = (offer: any) => {
    if (offer?.no_of_transactions === 1 && offer?.offer_type !== "BC") {
      setTab("one_time");
    } else {
      setTab("schedule");
    }
    setActiveOffer(offer.offer_type);
    setSelectedOffer(offer);
  };

  useEffect(() => {
    if (activeOffer === "BC") {
      wasBudgetCalculatorOpenRef.current = true;
      budgetCalculatorHeadingRef.current?.focus();
      return;
    }

    if (
      wasBudgetCalculatorOpenRef.current &&
      selectedOffer?.id !== undefined &&
      selectedOffer?.id !== null
    ) {
      offerInputRefs.current[String(selectedOffer.id)]?.focus();
      wasBudgetCalculatorOpenRef.current = false;
    }
  }, [activeOffer, selectedOffer?.id]);

  const renderIcon = (offerType: string) => {
    switch (offerType) {
      case "CO":
        return <OfferIcon className="mx-auto" />;
      case "BC":
        return <BudgetCalculatorIcon className="mx-auto" />;
      case "MI":
        return <RankingIcon className="mx-auto" />;
      default:
        return null;
    }
  };

  const handleOfferSubmitBtn = () => {
    if (activeOffer === "BC") {
      setActiveOffer("");
      setBudgetCalculatorIncome(0);
    } else {
      setTab("one_time");
      setAvailableOffer(false);
      setActiveOffer("");
    }
  };

  const paymentAssistance = useMemo(() => {
    const noAssistance: PaymentAssistanceState = {
      isNeeded: false,
      reason: null,
    };

    if (activeOffer === "BC" && budgerCalculatorIncome) {
      if (incomeAndExpense.netIncome < 0) {
        return {
          isNeeded: true,
          reason: "NEGATIVE_NET_INCOME" as const,
        };
      } else if (
        budgerCalculatorIncome > userData?.data?.accountData?.balance
      ) {
        return noAssistance;
      } else {
        const { min, max } = formatMinAndMaxAmount(
          generalSettings?.systemSettings,
          userData?.data?.accountData?.balance
        );
        if (budgerCalculatorIncome < min || budgerCalculatorIncome > max)
          return {
            isNeeded: true,
            reason: "PAYMENT_AMOUNT_OUTSIDE_RANGE" as const,
          };

        const limitResult = getBudgetCalculatorLimitResult({
          balance: userData?.data?.accountData?.balance,
          monthlyAmount: budgerCalculatorIncome,
          configuredTransactionLimit: portalSettings?.transaction_limit,
          configuredEndDateLimit: portalSettings?.end_date_limit_external,
        });

        if (limitResult.isExceeded && limitResult.reason) {
          return {
            isNeeded: true,
            reason: limitResult.reason,
          };
        }
      }
    }
    return noAssistance;
  }, [
    budgerCalculatorIncome,
    activeOffer,
    generalSettings?.systemSettings,
    incomeAndExpense.netIncome,
    portalSettings?.end_date_limit_external,
    portalSettings?.transaction_limit,
    userData?.data?.accountData?.balance,
  ]);
  const needsPaymentAssistance = paymentAssistance.isNeeded;
  const paymentAssistanceMessage = paymentAssistance.reason
    ? getBudgetCalculatorAssistanceMessage(paymentAssistance.reason)
    : "";

  const selectedOfferTransactionError = useMemo(
    () =>
      getNonBudgetOfferTransactionLimitError(
        selectedOffer,
        portalSettings?.transaction_limit
      ),
    [selectedOffer, portalSettings?.transaction_limit]
  );

  const onSubmit = () => {
    const { income, expense, netIncome } = incomeAndExpense;
    handleSendAssistanceRequest(income, expense, netIncome);
  };

  return (
    <div className="h-full lg:flex flex-col w-full">
      <div
        className={`${
          activeOffer === "BC" ? "md:px-10 px-5" : "w-full px-0"
        } lg:pt-[90px] py-5 lg:h-[90%] lg:overflow-y-scroll `}
      >
        {availableStateLoading ? (
          <div className="max-w-[500px] w-full lg:px-10 px-5 ">
            <Skeleton width={150} height={15} className="mb-10" />
            <Skeleton
              count={3}
              className="mb-6 w-full max-w-[505px] h-[204px]"
            />
          </div>
        ) : (
          <>
            {activeOffer === "BC" ? (
              <BudgetCalc
                setBudgetCalculatorIncome={setBudgetCalculatorIncome}
                needsPaymentAssistance={needsPaymentAssistance}
                paymentAssistanceMessage={paymentAssistanceMessage}
                setIncomeAndExpense={setIncomeAndExpense}
                headingRef={budgetCalculatorHeadingRef}
              />
            ) : (
              <fieldset className="max-w-[500px] w-full border-0 m-0 lg:px-10 px-5 p-0">
                <legend className="lg:text-xl font-medium lg:mb-10 mb-5 text-txtPrimary">
                  Available Offers
                </legend>
                {availableOfferData.map((offer: any, index: number) => {
                  const offerKey = String(offer.id);
                  const inputId = `${offerGroupId}-offer-${index}`;
                  const headlineId = `${inputId}-headline`;
                  const descriptionId = `${inputId}-description`;
                  const isSelected =
                    selectedOffer?.id !== undefined &&
                    selectedOffer?.id !== null &&
                    String(selectedOffer.id) === offerKey;

                  return (
                    <div
                      key={offer.id}
                      className="relative w-full max-w-[505px] lg:mb-6 mb-5"
                    >
                      <input
                        ref={(element) => {
                          offerInputRefs.current[offerKey] = element;
                        }}
                        id={inputId}
                        type="radio"
                        name={`${offerGroupId}-available-offer`}
                        value={offerKey}
                        checked={isSelected}
                        onChange={() => handleOfferChange(offer)}
                        onKeyDown={(event) => {
                          if (event.key !== "Enter") return;
                          event.preventDefault();
                          handleOfferChange(offer);
                        }}
                        aria-labelledby={headlineId}
                        aria-describedby={descriptionId}
                        className="peer sr-only"
                      />
                      <label
                        htmlFor={inputId}
                        className={`relative block w-full rounded-lg text-center 1xl:px-24 xl:px-14 lg:px-10 px-5 py-8 cursor-pointer peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-blue ${
                          isSelected
                            ? "border-2 !border-btnOutlineStroke !bg-btnOutlineHover"
                            : "border bg-bgThird hover:!bg-bgSecondary hover:border-strokeActive"
                        }`}
                      >
                        <span aria-hidden="true">
                          {renderIcon(offer.offer_type)}
                        </span>
                        <span
                          id={headlineId}
                          className="block lg:text-xl text-lg text-txtPrimary font-bold my-3"
                        >
                          {offer.headline}
                        </span>
                        <span
                          id={descriptionId}
                          className="block lg:text-base text-sm text-txtParagraph font-medium"
                        >
                          {offer?.description || "--"}
                        </span>
                      </label>
                    </div>
                  );
                })}
                {selectedOfferTransactionError && (
                  <p className="text-red text-xs" role="alert">
                    {selectedOfferTransactionError.message}
                  </p>
                )}
              </fieldset>
            )}
          </>
        )}
      </div>

      <div className="flex justify-between border-t border-strokePrimary lg:px-10 px-5 gap-5 py-5 w-full bg-bgPrimary z-10">
        {activeOffer === "BC" ||
        portalSettings?.allow_skip_offer === SettingsValue.YES ? (
          <Button
            text={`${activeOffer === "BC" ? "Back " : "Skip Offer"}`}
            variant="secondary"
            className="lg:w-40 w-full"
            onClick={() => {
              if (activeOffer !== "BC") {
                setSelectedOffer(undefined);
                handleOfferSubmitBtn();
              } else {
                handleOfferSubmitBtn();
              }
            }}
          />
        ) : (
          <div className="lg:w-40 lg:block hidden"></div>
        )}

        {needsPaymentAssistance ? (
          <Button
            text="Request payment assistance"
            className="max-w-[300px] w-full"
            variant="variable"
            onClick={onSubmit}
            isLoading={isAssistanceLoading}
          />
        ) : (
          <Button
            text="Next"
            className="lg:w-40 w-full"
            isDisabled={
              !activeOffer ||
              Boolean(selectedOfferTransactionError) ||
              (activeOffer === "BC" && budgerCalculatorIncome <= 0) ||
              incomeAndExpense.netIncome < 0
            }
            variant="variable"
            onClick={() => {
              if (selectedOfferTransactionError) return;

              setDataForSelectedOffer(selectedOffer);
              setAvailableOffer(false);
              setOfferStep(2);
              setTab(
                `${
                  selectedOffer.offer_type === "BC" ||
                  selectedOffer?.frequency_type !== "R"
                    ? "schedule"
                    : "one_time"
                }`
              );
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AvailableOffer;
